import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { gradeText } from "@/lib/gradeLabel";
import "./ClassGradebook.css";

/**
 * Teacher score grid for one class.
 * Remove this file, ClassGradebook.css, the gradebook route, classGradebookService,
 * and the Gradebook tab in ClassWorkspace to take it back out.
 */

type Learner = {
  uisUgpId: string;
  person: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  grade?: { percent: number | null; letter: string | null };
};

type Column = {
  id: string;
  title: string;
  type: string;
  pointsPossible: number;
};

type Cell = {
  activityId: string;
  learnerUgpId: string;
  earned: number | null;
  possible: number | null;
  progressId: string | null;
  submissionId: string | null;
};

function learnerName(row: Learner) {
  const name = [row.person?.firstName, row.person?.lastName].filter(Boolean).join(" ").trim();
  return name || row.person?.email || "Student";
}

function learnerSort(row: Learner) {
  return [
    (row.person?.lastName || "").toLowerCase(),
    (row.person?.firstName || "").toLowerCase(),
    learnerName(row).toLowerCase(),
  ].join("\t");
}

function num(value: number) {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

function scoreText(earned: number, possible: number) {
  return `${num(earned)}/${num(possible)}`;
}

export default function ClassGradebook({
  courseId,
  learners,
}: {
  courseId: string;
  learners: Learner[];
}) {
  const [columns, setColumns] = useState<Column[] | null>(null);
  const [cells, setCells] = useState<Cell[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setColumns(null);
    setError("");
    api<{ success: boolean; data?: { columns?: Column[]; cells?: Cell[] } }>(
      `/api/teaching/classes/${encodeURIComponent(courseId)}/gradebook`,
    )
      .then((envelope) => {
        if (cancel) return;
        setColumns(envelope.data?.columns || []);
        setCells(envelope.data?.cells || []);
      })
      .catch((err) => {
        if (cancel) return;
        setColumns([]);
        setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [courseId]);

  const byStudent = useMemo(() => {
    const map = new Map<string, Map<string, Cell>>();
    for (const cell of cells) {
      const row = map.get(cell.learnerUgpId) || new Map<string, Cell>();
      row.set(cell.activityId, cell);
      map.set(cell.learnerUgpId, row);
    }
    return map;
  }, [cells]);

  const students = useMemo(
    () => [...learners].sort((a, b) => learnerSort(a).localeCompare(learnerSort(b))),
    [learners],
  );

  if (columns == null) {
    return <p className="al-gradebook-status">Loading the gradebook…</p>;
  }
  if (error) {
    return <p className="al-gradebook-status">{error}</p>;
  }
  if (!students.length) {
    return <p className="al-gradebook-status">No students are enrolled in this class.</p>;
  }
  if (!columns.length) {
    return (
      <p className="al-gradebook-status">
        Nothing in this class can receive a score yet. Readings and other work without points stay on the path.
      </p>
    );
  }

  return (
    <div className="al-gradebook">
      <div className="al-gradebook-scroll">
        <table>
          <thead>
            <tr>
              <th className="al-gradebook-name" scope="col">Student</th>
              {columns.map((column) => (
                <th key={column.id} scope="col" title={column.title}>
                  <span className="al-gradebook-col-title">{column.title}</span>
                  <span className="al-gradebook-col-pts">{column.pointsPossible} pts</span>
                </th>
              ))}
              <th className="al-gradebook-class" scope="col">Class</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => {
              const scores = byStudent.get(student.uisUgpId);
              const courseGrade = student.grade?.percent != null ? gradeText(student.grade) : null;
              return (
                <tr key={student.uisUgpId}>
                  <th className="al-gradebook-name" scope="row">{learnerName(student)}</th>
                  {columns.map((column) => {
                    const cell = scores?.get(column.id);
                    const earned = cell?.earned;
                    const possible = cell?.possible != null && cell.possible > 0 ? cell.possible : column.pointsPossible;
                    const text = earned == null ? "-" : scoreText(earned, possible);
                    const href = cellHref(courseId, column, cell, student.uisUgpId);
                    return (
                      <td key={column.id}>
                        {href && earned != null ? (
                          <Link to={href} title={`${column.title}: ${text}`}>{text}</Link>
                        ) : (
                          <span className="al-gradebook-empty" title={cell ? "No score yet" : "Not started"}>{text}</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="al-gradebook-class">{courseGrade || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function cellHref(courseId: string, column: Column, cell: Cell | undefined, learnerUgpId: string) {
  if (!cell) return null;
  const base = `/teach/${encodeURIComponent(courseId)}`;
  if (column.type === "discussion") {
    return `${base}/discussions/${encodeURIComponent(column.id)}?student=${encodeURIComponent(learnerUgpId)}`;
  }
  if (cell.submissionId) return `${base}/work/${encodeURIComponent(cell.submissionId)}`;
  if (cell.progressId) return `${base}/progress/${encodeURIComponent(cell.progressId)}`;
  return null;
}
